
import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { BidStrategyDropdown } from '@bingads-webui-campaign-react/bid-strategy-dropdown';
import { TargetCpaWarningBanner } from '@bingads-webui-campaign-react/target-cpa-warning-banner';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';

import { AdditionalDetails } from './additional-details';
import { BidMessageBar } from './bid-message-bar';
import { BidStrategyShimmer } from './bid-strategy-shimmer';
import { getBidStrategySchema } from './options/use-bid-strategy-schema';

export const BidStrategyComponent = ({
  // Dropdown
  options,
  selectedBidStrategy,
  onBidStrategyChange,
  onRenderInlineDropdownHelp,
  // AdditionalDetails
  hideAdditionalDetails,
  defaultChecked,
  marketingMetricsValue,
  inputUnit,
  bidRange,
  onMarketingMetricChange,
  // MessageBar
  messageBarType,
  messageContent,
  onButtonClick,
  buttonText,
  isLoading,
  hideMessage,
  // TargetCPAWarningBanner
  withTargetCpaWarning,
  budgetAmount,
  scenario,
}) => {
  const instrumentation = useContext(InstrumentationContext);
  const activity = useMemo(() => {
    const { activity: scenarioActivity } = instrumentation.createScenarioAndActivity({
      scenarioName: scenario,
      activityName: 'BidStrategyComponent',
    });
    return scenarioActivity;
  }, [scenario, instrumentation]);
  const schema = useMemo(() => getBidStrategySchema(options, bidRange), [options, bidRange]);

  const bidStrategyConfig = options.find(({ type }) => type === selectedBidStrategy);
  const {
    key: selectedKey,
    inputLabel,
    inputUnit: defaultUnit,
    checkboxLabel,
  } = bidStrategyConfig || {};

  return (
    isLoading
      ? <BidStrategyShimmer />
      : (
        <div className="bid-strategy-component">
          <BidStrategyDropdown
            bidStrategyOptions={options}
            onBidStrategyChange={onBidStrategyChange}
            bidStrategyValue={selectedKey}
            defaultSelectedKey={selectedKey}
            onRenderInlineHelp={onRenderInlineDropdownHelp}
            bidStrategyLabel={_TL_('Choose which bid strategy you want to use to manage your daily budget.')}
          />
          {!hideAdditionalDetails && <AdditionalDetails
            defaultChecked={defaultChecked}
            checkboxLabel={checkboxLabel}
            inputLabel={inputLabel}
            inputUnit={inputUnit || defaultUnit}
            marketingMetricsValue={marketingMetricsValue}
            onMarketingMetricChange={onMarketingMetricChange}
            schema={schema}
            bidStrategy={selectedBidStrategy}
          />}
          {withTargetCpaWarning && <TargetCpaWarningBanner
            budgetAmount={budgetAmount}
            cpa={_.get(marketingMetricsValue, 'cpa')}
            activity={activity}
            scenario={scenario}
            isSignup
          />}
          {!hideMessage && <BidMessageBar
            messageBarType={messageBarType}
            messageContent={messageContent}
            onButtonClick={onButtonClick}
            buttonText={buttonText}
          />}
        </div>
      )
  );
};

BidStrategyComponent.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    inputLabel: PropTypes.string.isRequired,
    inputUnit: PropTypes.string.isRequired,
    checkboxLabel: PropTypes.string.isRequired,
  })).isRequired,
  onBidStrategyChange: PropTypes.func.isRequired,
  messageBarType: PropTypes.string,
  messageContent: PropTypes.string.isRequired,
  onButtonClick: PropTypes.func,
  buttonText: PropTypes.string,
  onRenderInlineDropdownHelp: PropTypes.func,
  onMarketingMetricChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  hideAdditionalDetails: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  marketingMetricsValue: PropTypes.shape({
    cpa: PropTypes.number,
    roas: PropTypes.number,
  }).isRequired,
  bidRange: PropTypes.shape({
    FloorValue: PropTypes.number,
    CeilingValue: PropTypes.number,
  }),
  selectedBidStrategy: PropTypes.string.isRequired,
  inputUnit: PropTypes.string,
  hideMessage: PropTypes.bool,
  budgetAmount: PropTypes.number,
  withTargetCpaWarning: PropTypes.bool,
  scenario: PropTypes.string,
};

BidStrategyComponent.defaultProps = {
  messageBarType: undefined,
  onButtonClick: undefined,
  buttonText: undefined,
  onRenderInlineDropdownHelp: _.noop,
  isLoading: false,
  hideAdditionalDetails: false,
  defaultChecked: false,
  inputUnit: 'USD',
  hideMessage: false,
  budgetAmount: undefined,
  withTargetCpaWarning: false,
  scenario: '',
  bidRange: null,
};
